import * as R from 'ramda'
import React, { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { Modal, Button, ReorderIcon, ResetIcon } from 'examkrackers-components'

import { getCurrentCourse } from 'modules/courses/ducks/selectors'
import { COURSE_TYPES, getCourseRemainingDays } from 'utils/courses'
import PATHS from 'utils/paths'
import { closeReorganiseAfterExtendModal } from 'services/CourseService'
import { fetchCurrentCourseRoutine } from 'modules/courses/ducks/actions'
import { DAYS_AMOUNT_TO_REORGANISE } from './utils/calendarDaysAmount'

const ModalReorganiseAfterExtend = () => {
  const { push } = useHistory()
  const dispatch = useDispatch()

  const [open, setOpen] = React.useState(false)

  const fetchCurrentCourse = React.useCallback(
    () => dispatch(fetchCurrentCourseRoutine()),
    [dispatch]
  )

  const currentCourse = useSelector(getCurrentCourse)
  const isLiveCourse = R.pipe(
    R.propOr('', 'type'),
    R.equals(COURSE_TYPES.live)
  )(currentCourse)

  const isExtendedFromFreeTrial = R.pipe(
    R.propOr('{}', 'metadata'),
    JSON.parse,
    R.propOr('', 'extended_from_type'),
    R.equals('free_trial')
  )(currentCourse)

  const isExtendedFromSubscription = R.pipe(
    R.propOr('{}', 'metadata'),
    JSON.parse,
    R.propOr('', 'extended_from_type'),
    R.equals('on_demand')
  )(currentCourse)

  const isCourseExtended = isExtendedFromFreeTrial || isExtendedFromSubscription
  const isExtendedByMinimumDays =
    getCourseRemainingDays(currentCourse) >= DAYS_AMOUNT_TO_REORGANISE
  const isModalClosed = R.propOr(
    false,
    'extension_calendar_modal_closed',
    currentCourse
  )

  const handleOpen = () => setOpen(true)
  const handleClose = () => {
    setOpen(false)
    closeReorganiseAfterExtendModal()
      .then(() => {
        fetchCurrentCourse()
      })
      .catch(error => {
        console.error(error)
      })
  }

  useEffect(() => {
    if (isCourseExtended && isExtendedByMinimumDays && !isModalClosed) {
      handleOpen()
    }
  }, [currentCourse])

  const goToReorder = () => {
    handleClose()
    if (isLiveCourse) {
      push(PATHS.calendarReorderForExpiration)
    } else {
      push({ pathname: PATHS.calendarReorder, state: { isLiveCourse } })
    }
  }

  const goToReset = () => {
    handleClose()
    if (isLiveCourse) {
      push(PATHS.calendarResetForExpiration)
    } else {
      push({ pathname: PATHS.calendarReset, state: { isLiveCourse } })
    }
  }

  const { t } = useTranslation()

  return (
    <Modal
      title={t('calendar.reorganiseAfterExtend.title')}
      open={open}
      handleClose={handleClose}
      hasBorder
    >
      <ModalContainer>
        <Description>
          {isExtendedFromFreeTrial
            ? t('calendar.reorganiseAfterExtend.freeTrial.description')
            : t('calendar.reorganiseAfterExtend.subscription.description')}
        </Description>
        {isExtendedFromFreeTrial && (
          <Description2>
            {t('calendar.reorganiseAfterExtend.freeTrial.description2')}
          </Description2>
        )}
        <ExplanationContainer>
          <Explanation>
            <ExplanationText>
              <Trans i18nKey='calendar.reorganiseAfterExtend.reorderDescription'>
                <span className='explanation-reorder'>Reorder:</span> 1
                <span className='explanation-underline'>incomplete events</span>
                3
              </Trans>
            </ExplanationText>
            <ExplanationText>
              <Trans i18nKey='calendar.reorganiseAfterExtend.resetDescription'>
                <span className='explanation-reset'>Reset:</span>1
                <span className='explanation-underline'>all events</span> 3
              </Trans>
            </ExplanationText>
          </Explanation>
          <ExplanationImageContainer>
            <ExplanationImage src='/assets/illustrations/reorganiseSalty.svg' />
          </ExplanationImageContainer>
        </ExplanationContainer>
        <ButtonsContainer>
          <Button
            color='tertiary'
            size='small'
            id='close-reorganise-modal'
            onClick={handleClose}
          >
            {t(
              `calendar.reorganiseAfterExtend.${
                isExtendedFromFreeTrial ? 'freeTrial' : 'subscription'
              }.cancel`
            )}
          </Button>
          <Button
            color='primary'
            size='small'
            id='reorder-modal'
            onClick={goToReorder}
            startIcon={<ReorderIcon />}
          >
            {t('calendar.reorganiseAfterExtend.reorder')}
          </Button>
          <Button
            color='secondary'
            size='small'
            id='reset-modal'
            onClick={goToReset}
            startIcon={<ResetIcon />}
          >
            {t('calendar.reorganiseAfterExtend.reset')}
          </Button>
        </ButtonsContainer>
      </ModalContainer>
    </Modal>
  )
}

export default ModalReorganiseAfterExtend

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Description = styled.div`
  max-width: 339px;
  font-weight: 400;
  font-size: 16px;
  margin-bottom: 20px;
`
const Description2 = styled.div`
  max-width: 339px;
  font-weight: 400;
  font-size: 14px;
  margin-bottom: 40px;
`

const ExplanationContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 570px;
  justify-content: space-around;
`

const Explanation = styled.div`
  text-align: left;
`

const ExplanationText = styled.div`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  max-width: 339px;
  margin-bottom: 10px;

  .explanation-reorder {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.main.primary500};
  }

  .explanation-reset {
    font-weight: 700;
    color: ${({ theme }) => theme.colors.main.primary800};
  }

  .explanation-underline {
    text-decoration: underline !important;
  }
`

const ExplanationImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const ExplanationImage = styled.img`
  width: 165px;
  flex-grow: 1;
`

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;

  button {
    min-width: 150px;
  }
`
